import { createContext, useContext, useEffect, useState, useMemo } from "react";

const TRANSLATIONS = {
    en: {
        nav: {
            worklines: "Worklines",
            newYear: "New Year",
            renewable: "Renewable",
            students: "Students",
            schools: "Schools",
            about: "About",
            monthlyChapters: "Monthly chapters",
            more: "More",
        },
        common: {
            allThemes: "All themes",
            backHome: "Back home",
            clear: "Clear",
            search: "Search posts, students, schools…",
            allSchools: "All schools",
            allCountries: "All countries",
            contribution: "contribution",
            contributions: "contributions",
            total: "total",
            entries: "entries",
            of: "of",
            featured: "May Highlights",
            mostImportantMonth: "Our most important month",
            seeAll: "See all",
            exploreWorkline: "Explore the Workline",
            seeAllThemes: "See all themes",
            browseSchools: "Browse all schools",
            visit: "Visit the project on eTwinning",
            themeNotFound: "Theme not found.",
            noMatches: "No matches yet.",
            tryDifferent: "Try clearing filters or searching with different keywords.",
            comments: "Comments",
            addComment: "Leave a comment",
            yourName: "Your name",
            yourComment: "Your comment…",
            attachFile: "Attach file",
            send: "Send",
            sending: "Sending…",
            noComments: "Be the first to leave a thought.",
            attachment: "attachment",
            uploading: "Uploading…",
            anonymous: "Anonymous",
            joinFacebook: "Join the Facebook group",
            facebookGroup: "Facebook group",
            soundOn: "Sound on",
            soundOff: "Sound off",
            musicOn: "Music on",
            musicOff: "Music off",
        },
        live: {
            activeUsers: "active right now",
            totalVisits: "total visits",
            uniqueVisitors: "unique visitors",
        },
        home: {
            badge: "December → May",
            heroIntroPrefix: "STEMcycle G2B",
            heroSubtitle: "Green to Blue Innovation Lab",
            statsContributions: "Contributions",
            statsSchools: "Partner Schools",
            statsCountries: "Countries",
            statsThemes: "Themes",
            themesEyebrow: "/ Themes",
            themesHeading1: "Five chapters of a single,",
            themesHeading2: "unfolding story.",
            themesBody: "Student voices on mountains, oceans, energy and the bridges between them — collected, curated and gently arranged for slow reading.",
            manifestoEyebrow: "/ Manifesto",
            manifestoQuote: "“Green to Blue is not only a colour transition, it is a transition of habits — of how we consume, how we build, and how we share resources with nature.”",
            manifestoSource: "— Student reflection, December Workline",
            schoolsEyebrow: "/ Partner Schools",
            schoolsHeading1: "From Ankara to Bucharest, Terjola to Travnik —",
            schoolsHeading2: "one classroom, one ocean.",
        },
        about: {
            eyebrow: "/ About",
            heading1: "A small workline.",
            heading2: "A wide horizon.",
            workline: "The Workline",
            worklineBody: "Across seven months (December → May) the workline collects student responses to World Mountain Day, the Green-to-Blue Innovation Lab, Renewable Energy, the New Year Sustainability Call, Earth Day & Blue Economy Research, and eTwinning Day.",
            partners: "The Partners",
            partnersBody: "Co-founded by partner schools in Türkiye and Romania, with general and vocational high-schools from Georgia, Lithuania and Bosnia & Herzegovina — coordinated through the eTwinning platform.",
            method: "The Method",
            methodBody: "Slow reading, mindful sharing. Each contribution is presented like a museum placard — name, school, country, voice.",
            goal: "The Goal",
            goalBody: "To make students' sustainability thinking visible, durable and celebrated — not buried in a slideshow.",
        },
        schools: {
            eyebrow: "/ Directory",
            heading1: "Participating",
            heading2: "Schools.",
            description: (n, c) => `${n} schools from ${c} countries — a quiet chorus of classrooms, each contributing a verse to the Green-to-Blue story.`,
            tableSchool: "School",
            tableCountry: "Country",
            tableContributions: "Contributions",
            searchPlaceholder: "Search schools or countries…",
            noMatch: "No schools match your search.",
        },
    },
    tr: {
        nav: {
            worklines: "Çalışma Hatları",
            newYear: "Yeni Yıl",
            renewable: "Yenilenebilir",
            students: "Öğrenciler",
            schools: "Okullar",
            about: "Hakkında",
            monthlyChapters: "Aylık bölümler",
            more: "Daha fazla",
        },
        common: {
            allThemes: "Tüm temalar",
            backHome: "Ana sayfaya dön",
            clear: "Temizle",
            search: "Gönderi, öğrenci veya okul ara…",
            allSchools: "Tüm okullar",
            allCountries: "Tüm ülkeler",
            contribution: "katkı",
            contributions: "katkı",
            total: "toplam",
            entries: "giriş",
            of: "/",
            featured: "Mayıs Öne Çıkanlar",
            mostImportantMonth: "En önemli ayımız",
            seeAll: "Tümünü gör",
            exploreWorkline: "Çalışma Hattını Keşfet",
            seeAllThemes: "Tüm temaları gör",
            browseSchools: "Tüm okullara göz at",
            visit: "Projeyi eTwinning'de görüntüle",
            themeNotFound: "Tema bulunamadı.",
            noMatches: "Henüz eşleşme yok.",
            tryDifferent: "Filtreleri temizleyin veya farklı kelimelerle arayın.",
            comments: "Yorumlar",
            addComment: "Bir yorum bırakın",
            yourName: "Adınız",
            yourComment: "Yorumunuz…",
            attachFile: "Dosya ekle",
            send: "Gönder",
            sending: "Gönderiliyor…",
            noComments: "İlk düşünceyi bırakan siz olun.",
            attachment: "ek",
            uploading: "Yükleniyor…",
            anonymous: "Anonim",
            joinFacebook: "Facebook grubuna katıl",
            facebookGroup: "Facebook grubu",
            soundOn: "Ses açık",
            soundOff: "Ses kapalı",
            musicOn: "Müzik açık",
            musicOff: "Müzik kapalı",
        },
        live: {
            activeUsers: "şu anda aktif",
            totalVisits: "toplam ziyaret",
            uniqueVisitors: "tekil ziyaretçi",
        },
        home: {
            badge: "Aralık → Mayıs",
            heroIntroPrefix: "STEMcycle G2B",
            heroSubtitle: "Yeşilden Maviye İnovasyon Laboratuvarı",
            statsContributions: "Katkı",
            statsSchools: "Ortak Okul",
            statsCountries: "Ülke",
            statsThemes: "Tema",
            themesEyebrow: "/ Temalar",
            themesHeading1: "Tek bir hikayenin",
            themesHeading2: "yedi bölümü.",
            themesBody: "Öğrencilerin dağlar, okyanuslar, enerji ve aradaki köprüler üzerine sesleri — özenle derlenmiş, yavaş okumak için düzenlenmiş.",
            manifestoEyebrow: "/ Manifesto",
            manifestoQuote: "“Yeşilden Maviye yalnızca bir renk geçişi değildir; alışkanlıkların geçişidir — nasıl tükettiğimizin, nasıl inşa ettiğimizin ve doğayla nasıl paylaştığımızın.”",
            manifestoSource: "— Öğrenci yansıması, Aralık Çalışma Hattı",
            schoolsEyebrow: "/ Ortak Okullar",
            schoolsHeading1: "Ankara'dan Bükreş'e, Terjola'dan Travnik'e —",
            schoolsHeading2: "tek bir sınıf, tek bir okyanus.",
        },
        about: {
            eyebrow: "/ Hakkında",
            heading1: "Küçük bir çalışma hattı.",
            heading2: "Geniş bir ufuk.",
            workline: "Çalışma Hattı",
            worklineBody: "Yedi ay boyunca (Aralık → Mayıs) Dünya Dağlar Günü, Yeşilden Maviye İnovasyon Laboratuvarı, Yenilenebilir Enerji, Yeni Yıl Sürdürülebilirlik Çağrısı, Dünya Günü & Mavi Ekonomi Araştırması ve eTwinning Günü temaları işlenir.",
            partners: "Ortaklar",
            partnersBody: "Türkiye ve Romanya'daki ortak okulların eş kurucu olduğu proje; İtalya, Gürcistan, Litvanya ve Bosna-Hersek'teki ortak liselerle eTwinning platformu üzerinden koordine edilir.",
            method: "Yöntem",
            methodBody: "Yavaş okuma, özenli paylaşım. Her katkı bir müze etiketi gibi sunulur — ad, okul, ülke, ses.",
            goal: "Hedef",
            goalBody: "Öğrencilerin sürdürülebilirlik düşüncelerini görünür, kalıcı ve kutlamaya değer kılmak — bir slayt gösterisinde gömülü bırakmak değil.",
        },
        schools: {
            eyebrow: "/ Rehber",
            heading1: "Katılımcı",
            heading2: "Okullar.",
            description: (n, c) => `${c} ülkeden ${n} okul — her biri Yeşilden Maviye hikayesine bir dize ekleyen sessiz bir sınıf korosu.`,
            tableSchool: "Okul",
            tableCountry: "Ülke",
            tableContributions: "Katkı",
            searchPlaceholder: "Okul veya ülke ara…",
            noMatch: "Aramanızla eşleşen okul yok.",
        },
    },
    ro: {
        nav: {
            worklines: "Linii de Lucru",
            newYear: "Anul Nou",
            renewable: "Regenerabilă",
            students: "Elevi",
            schools: "Școli",
            about: "Despre",
            monthlyChapters: "Capitole lunare",
            more: "Mai mult",
        },
        common: {
            allThemes: "Toate temele",
            backHome: "Înapoi acasă",
            clear: "Șterge",
            search: "Caută postări, elevi, școli…",
            allSchools: "Toate școlile",
            allCountries: "Toate țările",
            contribution: "contribuție",
            contributions: "contribuții",
            total: "total",
            entries: "intrări",
            of: "/",
            featured: "Recomandări Mai",
            mostImportantMonth: "Cea mai importantă lună",
            seeAll: "Vezi tot",
            exploreWorkline: "Explorează linia de lucru",
            seeAllThemes: "Vezi toate temele",
            browseSchools: "Vezi toate școlile",
            visit: "Vizitează proiectul pe eTwinning",
            themeNotFound: "Tema nu a fost găsită.",
            noMatches: "Niciun rezultat încă.",
            tryDifferent: "Șterge filtrele sau caută cu alte cuvinte cheie.",
            comments: "Comentarii",
            addComment: "Lasă un comentariu",
            yourName: "Numele tău",
            yourComment: "Comentariul tău…",
            attachFile: "Atașează fișier",
            send: "Trimite",
            sending: "Se trimite…",
            noComments: "Fii primul care lasă un gând.",
            attachment: "atașament",
            uploading: "Se încarcă…",
            anonymous: "Anonim",
            joinFacebook: "Alătură-te grupului Facebook",
            facebookGroup: "Grup Facebook",
            soundOn: "Sunet pornit",
            soundOff: "Sunet oprit",
            musicOn: "Muzică pornită",
            musicOff: "Muzică oprită",
        },
        live: {
            activeUsers: "activi acum",
            totalVisits: "vizite totale",
            uniqueVisitors: "vizitatori unici",
        },
        home: {
            badge: "Decembrie → Mai",
            heroIntroPrefix: "STEMcycle G2B",
            heroSubtitle: "Laboratorul de Inovație Verde-Albastru",
            statsContributions: "Contribuții",
            statsSchools: "Școli partenere",
            statsCountries: "Țări",
            statsThemes: "Teme",
            themesEyebrow: "/ Teme",
            themesHeading1: "Șapte capitole",
            themesHeading2: "ale unei singure povești.",
            themesBody: "Vocile elevilor despre munți, oceane, energie și punțile dintre ele — adunate, curatoriate și aranjate cu grijă pentru o lectură liniștită.",
            manifestoEyebrow: "/ Manifest",
            manifestoQuote: "„Green to Blue nu este doar o tranziție de culoare, este o tranziție a obiceiurilor — a felului în care consumăm, construim și împărțim resursele cu natura.”",
            manifestoSource: "— Reflecție de elev, Linia de lucru Decembrie",
            schoolsEyebrow: "/ Școli partenere",
            schoolsHeading1: "De la Ankara la București, de la Terjola la Travnik —",
            schoolsHeading2: "o clasă, un ocean.",
        },
        about: {
            eyebrow: "/ Despre",
            heading1: "O mică linie de lucru.",
            heading2: "Un orizont larg.",
            workline: "Linia de Lucru",
            worklineBody: "Pe parcursul a șapte luni (Decembrie → Mai), elevii răspund la Ziua Mondială a Munților, Laboratorul de Inovație Verde-Albastru, Energia Regenerabilă, Apelul de Sustenabilitate de Anul Nou, Ziua Pământului & cercetarea Economiei Albastre, și Ziua eTwinning.",
            partners: "Partenerii",
            partnersBody: "Co-fondat de școli partenere din Turcia și România, alături de licee generale și vocaționale din Italia, Georgia, Lituania și Bosnia și Herțegovina — coordonate prin platforma eTwinning.",
            method: "Metoda",
            methodBody: "Citire lentă, partajare atentă. Fiecare contribuție este prezentată ca o etichetă de muzeu — nume, școală, țară, voce.",
            goal: "Obiectivul",
            goalBody: "Să facem vizibilă, durabilă și sărbătorită gândirea elevilor despre sustenabilitate — nu îngropată într-un slideshow.",
        },
        schools: {
            eyebrow: "/ Director",
            heading1: "Școlile",
            heading2: "participante.",
            description: (n, c) => `${n} școli din ${c} țări — un cor liniștit de săli de clasă, fiecare adăugând un vers poveștii Green-to-Blue.`,
            tableSchool: "Școală",
            tableCountry: "Țară",
            tableContributions: "Contribuții",
            searchPlaceholder: "Caută școli sau țări…",
            noMatch: "Nicio școală nu corespunde căutării.",
        },
    },
};

const I18nContext = createContext({
    lang: "en",
    setLang: () => {},
    t: (path) => path,
});

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => {
        if (typeof window === "undefined") return "en";
        return window.localStorage.getItem("g2b_lang") || "en";
    });

    useEffect(() => {
        try {
            window.localStorage.setItem("g2b_lang", lang);
            document.documentElement.lang = lang;
        } catch (e) {
            /* noop */
        }
    }, [lang]);

    const t = useMemo(() => {
        const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
        return (path, ...args) => {
            const parts = path.split(".");
            let cur = dict;
            for (const p of parts) {
                if (cur && typeof cur === "object" && p in cur) cur = cur[p];
                else return path;
            }
            if (typeof cur === "function") return cur(...args);
            return cur;
        };
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
    return useContext(I18nContext);
}
